import type { UnifiedChatMessage } from '@/mocks/adminLiveChat';
import {
  sourceLabels,
  sourceIcons,
  sourceColors,
  typeLabels,
  typeIcons,
  typeColors,
  statusLabels,
  statusColors,
  priorityIcons,
  priorityColors,
} from '@/mocks/adminLiveChat';

interface Props {
  message: UnifiedChatMessage | null;
  onReply: (msg: UnifiedChatMessage) => void;
  onToggleFavorite: (msg: UnifiedChatMessage) => void;
  onTogglePin: (msg: UnifiedChatMessage) => void;
  onMarkAir: (msg: UnifiedChatMessage) => void;
  onMarkPending: (msg: UnifiedChatMessage) => void;
  onBlock: (msg: UnifiedChatMessage) => void;
  onBanIP: (msg: UnifiedChatMessage) => void;
  onArchive: (msg: UnifiedChatMessage) => void;
  onHide: (msg: UnifiedChatMessage) => void;
}

export default function UnifiedMessageDetail({
  message,
  onReply,
  onToggleFavorite,
  onTogglePin,
  onMarkAir,
  onMarkPending,
  onBlock,
  onBanIP,
  onArchive,
  onHide,
}: Props) {
  if (!message) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-8 py-16">
        <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-chat-3-line text-foreground-300 text-2xl"></i>
        </div>
        <h3 className="text-foreground-500 font-bold text-sm">Selecciona un mensaje</h3>
        <p className="text-foreground-400 text-xs mt-1 max-w-xs">
          Haz clic en cualquier mensaje de la lista para ver su detalle, metadata y acciones disponibles.
        </p>
      </div>
    );
  }

  const sourceStyle = sourceColors[message.source];
  const typeStyle = typeColors[message.type];
  const statusStyle = statusColors[message.status];

  const formatFullTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('es-CR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="flex flex-col h-full bg-background-50">
      {/* Detail header */}
      <div className="shrink-0 px-4 py-3 border-b border-background-200 bg-background-50">
        <div className="flex items-start gap-3">
          <div className="relative shrink-0">
            <div className="w-10 h-10 rounded-full bg-background-200 overflow-hidden">
              <img
                src={message.senderAvatar}
                alt={message.senderName}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center shadow-sm ${sourceStyle.bg}`}>
              <i className={`${sourceIcons[message.source]} text-[8px] ${sourceStyle.text}`}></i>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-bold text-foreground-950">{message.senderName}</h4>
              <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`}></span>
              <span className={`text-[10px] font-semibold ${statusStyle.text}`}>
                {statusLabels[message.status]}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${typeStyle.bg} ${typeStyle.text}`}>
                <i className={`${typeIcons[message.type]} text-[8px]`}></i>
                {typeLabels[message.type]}
              </span>
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-medium ${sourceStyle.bg} ${sourceStyle.text}`}>
                <i className={`${sourceIcons[message.source]} text-[8px]`}></i>
                {sourceLabels[message.source]}
              </span>
              {message.priority !== 'normal' && (
                <span className={`inline-flex items-center gap-0.5 text-[9px] font-semibold ${priorityColors[message.priority]}`}>
                  <i className={`${priorityIcons[message.priority]} text-[8px]`}></i>
                  {message.priority === 'urgente' ? 'Urgente' : 'Alta'}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Message content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {/* Source detail */}
        <div className="mb-3">
          <span className="text-[10px] text-foreground-400 font-medium">Origen:</span>
          <span className="ml-1 text-[10px] text-foreground-600 font-semibold">{message.sourceDetail}</span>
        </div>

        {/* Content bubble */}
        <div className="rounded-2xl bg-background-100 p-4 mb-3">
          <p className="text-foreground-800 text-xs leading-relaxed">{message.content}</p>
        </div>

        {/* Song request */}
        {message.songRequest && (
          <div className="flex items-center gap-2 mb-3 rounded-xl bg-emerald-50 border border-emerald-200 p-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
              <i className="ri-music-line text-emerald-600 text-sm"></i>
            </div>
            <div className="min-w-0">
              <p className="text-[9px] text-emerald-600 font-semibold uppercase tracking-wider">Canción solicitada</p>
              <p className="text-xs text-emerald-800 font-medium">{message.songRequest}</p>
            </div>
          </div>
        )}

        {/* Contest answer */}
        {message.contestAnswer && (
          <div className="flex items-center gap-2 mb-3 rounded-xl bg-amber-50 border border-amber-200 p-3">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <i className="ri-trophy-line text-amber-600 text-sm"></i>
            </div>
            <div className="min-w-0">
              <p className="text-[9px] text-amber-600 font-semibold uppercase tracking-wider">Respuesta del concurso</p>
              <p className="text-xs text-amber-800 font-medium">{message.contestAnswer}</p>
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="rounded-xl bg-background-50 border border-background-200 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-foreground-400">IP del oyente</span>
            <span className="text-[10px] text-foreground-600 font-mono font-medium">{message.senderIP}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-foreground-400">Recibido</span>
            <span className="text-[10px] text-foreground-600 font-medium">{formatFullTime(message.timestamp)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-foreground-400">Prioridad</span>
            <span className={`text-[10px] font-semibold capitalize ${
              message.priority === 'urgente' ? 'text-red-600' : message.priority === 'alta' ? 'text-amber-600' : 'text-foreground-500'
            }`}>
              {message.priority === 'urgente' ? 'Urgente' : message.priority === 'alta' ? 'Alta' : 'Normal'}
            </span>
          </div>
        </div>
      </div>

      {/* Actions bar */}
      <div className="shrink-0 px-4 py-3 border-t border-background-200 bg-background-50">
        <div className="grid grid-cols-4 gap-1.5">
          <button
            onClick={() => onReply(message)}
            className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl bg-secondary-50 text-secondary-700 text-[9px] font-semibold cursor-pointer hover:bg-secondary-100 transition-colors"
          >
            <i className="ri-reply-line text-secondary-500 text-sm"></i>
            Responder
          </button>
          <button
            onClick={() => onToggleFavorite(message)}
            className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl text-[9px] font-semibold cursor-pointer transition-colors ${
              message.status === 'favorito'
                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                : 'bg-background-100 text-foreground-500 hover:bg-amber-50 hover:text-amber-600'
            }`}
          >
            <i className={`${message.status === 'favorito' ? 'ri-star-fill' : 'ri-star-line'} ${message.status === 'favorito' ? 'text-amber-500' : 'text-foreground-400'} text-sm`}></i>
            Favorito
          </button>
          <button
            onClick={() => onTogglePin(message)}
            className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl text-[9px] font-semibold cursor-pointer transition-colors ${
              message.status === 'fijado'
                ? 'bg-violet-100 text-violet-700 hover:bg-violet-200'
                : 'bg-background-100 text-foreground-500 hover:bg-violet-50 hover:text-violet-600'
            }`}
          >
            <i className={`${message.status === 'fijado' ? 'ri-pushpin-fill' : 'ri-pushpin-line'} ${message.status === 'fijado' ? 'text-violet-500' : 'text-foreground-400'} text-sm`}></i>
            Fijar
          </button>
          <button
            onClick={() => onMarkAir(message)}
            className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl bg-background-100 text-foreground-500 text-[9px] font-semibold cursor-pointer hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
          >
            <i className="ri-broadcast-line text-foreground-400 text-sm"></i>
            Al aire
          </button>
        </div>
        <div className="flex items-center gap-1.5 mt-1.5">
          <button
            onClick={() => onMarkPending(message)}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-orange-50 text-orange-600 text-[10px] font-semibold cursor-pointer hover:bg-orange-100 transition-colors"
          >
            <i className="ri-time-line text-xs"></i>
            Pendiente
          </button>
          <button
            onClick={() => onBlock(message)}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-red-50 text-red-600 text-[10px] font-semibold cursor-pointer hover:bg-red-100 transition-colors"
          >
            <i className="ri-forbid-2-line text-xs"></i>
            Bloquear
          </button>
          <button
            onClick={() => onBanIP(message)}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-red-50 text-red-600 text-[10px] font-semibold cursor-pointer hover:bg-red-100 transition-colors"
          >
            <i className="ri-globe-line text-xs"></i>
            Banear IP
          </button>
          <button
            onClick={() => onArchive(message)}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-foreground-100 text-foreground-600 text-[10px] font-semibold cursor-pointer hover:bg-foreground-200 transition-colors"
          >
            <i className="ri-archive-line text-xs"></i>
            Archivar
          </button>
          <button
            onClick={() => onHide(message)}
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-foreground-100 text-foreground-500 cursor-pointer hover:bg-foreground-200 transition-colors"
            title="Ocultar"
          >
            <i className="ri-eye-off-line text-xs"></i>
          </button>
        </div>
      </div>
    </div>
  );
}